// A customer at a glance on the call (the call-handling parity, item 1): what its stat line and facts
// say, kept out of the component so they are tested without a browser. The server counts and
// scopes (customers.glance); this words it.

import { longDay, monthYear } from '../records/records-model.ts';
import type { CampaignRef } from '../settings/campaigns-model.ts';
import { tileOf, viewerWords, type Shown } from '../shared/photo-display-model.ts';

/** customers.glance. */
export interface Glance {
  customerId: string;
  since: string;
  jobs: { count: number; lastVisitOn: string | null };
  billedCents: number | null;
  membership: { name: string; endsOn: string; autoRenew: boolean } | null;
  openEstimates: { count: number; listed: { id: string; number: string; title: string; jobId: string }[] };
  equipment: {
    count: number;
    listed: {
      id: string;
      kind: string;
      make: string | null;
      model: string | null;
      installedOn: string | null;
    }[];
  };
  about: string | null;
  notes: { body: string; on: string }[];
  siteContact: { name: string; phone: string | null } | null;
  /** First touch (BL-008): the campaign of their earliest call or job that has one. */
  firstCampaign?: CampaignRef | null;
}

/** "Mar 2019". */
export const sinceWord = (g: Pick<Glance, 'since'>) => monthYear(g.since);

/** "1 job", "4 jobs", "No jobs yet". */
export const jobsWord = (g: Pick<Glance, 'jobs'>) =>
  g.jobs.count === 0 ? 'No jobs yet' : g.jobs.count === 1 ? '1 job' : `${g.jobs.count} jobs`;

/** "Last visit Sep 3, 2026", or nothing before the first. */
export const lastVisitWord = (g: Pick<Glance, 'jobs'>) =>
  g.jobs.lastVisitOn ? `Last visit ${longDay(g.jobs.lastVisitOn)}` : null;

/** "Comfort Club, renews Jan 15, 2027" or "…, ends Jan 15, 2027" (it doesn't renew by itself). */
export function membershipWord(m: NonNullable<Glance['membership']>): string {
  return `${m.name}, ${m.autoRenew ? 'renews' : 'ends'} ${longDay(m.endsOn)}`;
}

/** "Water heater · Rheem XE50 · 2019". */
export function equipmentWord(e: Glance['equipment']['listed'][number]): string {
  const makeModel = [e.make, e.model].filter(Boolean).join(' ');
  return [e.kind, makeModel || null, e.installedOn ? e.installedOn.slice(0, 4) : null]
    .filter(Boolean)
    .join(' · ');
}

/** "and 2 more" past the listed ones, or nothing. */
export function moreWord(count: number, listed: number): string | null {
  return count > listed ? `and ${count - listed} more` : null;
}

const PAST = new Set(['completed', 'cancelled']);
/** Recent jobs with the open ones first (BL-099), each part keeping the order it came in. */
export const openJobsFirst = <J extends { status: string }>(jobs: readonly J[]): J[] => [
  ...jobs.filter((j) => !PAST.has(j.status)),
  ...jobs.filter((j) => PAST.has(j.status)),
];

/** How many of their newest photos the glance shows (BL-131). */
export const GLANCE_PHOTOS = 6;
/** How many it asks customers.photos for: a few more, so photos still being prepared don't leave it short. */
export const GLANCE_PHOTOS_ASKED = 12;

/** One of customers.photos' answers, as the glance's strip and the viewer need it. */
export interface GlancePhoto extends Shown {
  id: string;
  jobId: string;
  jobNumber: string;
  category: string;
  caption: string | null;
  capturedAt: string | null;
  addedBy: string | null;
  annotated: boolean;
  otherCustomer?: boolean;
}

/**
 * The glance's strip (BL-131): the newest photos that have a picture to show, at most GLANCE_PHOTOS,
 * newest first as customers.photos gives them. Each is called what P-0161 calls it: its note, else a
 * plain name from its category ("Before photo"), never a file's name; with the day it was taken. A
 * list over several jobs can't number a job's photos ("Before 2"), so the strip doesn't.
 */
export function photoStrip<P extends GlancePhoto>(
  photos: readonly P[],
  timeZone: string,
): { photo: P; src: string; name: string; when: string | null }[] {
  const day = new Intl.DateTimeFormat('en-CA', { timeZone, dateStyle: 'medium' });
  return photos
    .flatMap((photo) => {
      const tile = tileOf(photo);
      if (tile.t !== 'picture') return [];
      return [
        {
          photo,
          src: tile.src,
          name: viewerWords(photo, photo.caption).title,
          when: photo.capturedAt ? day.format(new Date(photo.capturedAt)) : null,
        },
      ];
    })
    .slice(0, GLANCE_PHOTOS);
}
